import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import './CollectionsPage.css';
import { getCollections } from '../../firebase/collections';
import { db } from '../../firebase/config'; // adjust path as needed
import { useAuth } from '../../contexts/AuthContext';
import {
    collection,
    doc,
    getDoc,
    runTransaction,
    onSnapshot,
} from 'firebase/firestore';

/* ─── Helpers: localStorage-backed "did I like this?" ────────── */
const LIKED_KEY = 'jg_liked_products';

function getSavedLikes() {
    try { return new Set(JSON.parse(localStorage.getItem(LIKED_KEY)) || []); }
    catch { return new Set(); }
}

function saveLikes(set) {
    localStorage.setItem(LIKED_KEY, JSON.stringify([...set]));
}

/* ─── Firebase like helpers ──────────────────────────────────── */
async function toggleLikeForUser(productId, userId, isNowLiked) {
    const countRef = doc(db, 'likes', productId);
    const userLikeRef = doc(collection(countRef, 'users'), userId);

    await runTransaction(db, async (tx) => {
        const [countSnap, userLikeSnap] = await Promise.all([
            tx.get(countRef),
            tx.get(userLikeRef),
        ]);

        const currentCount = countSnap.exists() ? (countSnap.data().count || 0) : 0;
        const hasUserLike = userLikeSnap.exists();

        if (isNowLiked && !hasUserLike) {
            tx.set(countRef, { count: currentCount + 1 }, { merge: true });
            tx.set(userLikeRef, { likedAt: Date.now() });
            return;
        }

        if (!isNowLiked && hasUserLike) {
            tx.set(countRef, { count: Math.max(0, currentCount - 1) }, { merge: true });
            tx.delete(userLikeRef);
        }
    });
}

/* ─── Main Page ──────────────────────────────────────────────── */
function CollectionsPage() {
    const location = useLocation();
    const { currentUser } = useAuth();

    const [activeFilter, setActiveFilter] = useState(
        location.state?.filter || 'All Products'
    );
    const [likedProducts, setLikedProducts] = useState(new Set());
    const [likeCounts,    setLikeCounts]    = useState({});
    const [expandedImage, setExpandedImage] = useState(null);
    const [products,      setProducts]      = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [error,         setError]         = useState(null);
    const [visibleCount,  setVisibleCount]  = useState(9);

    useEffect(() => {
        if (location.state?.filter) setActiveFilter(location.state.filter);
    }, [location.state]);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        getCollections().then(({ collections: items, error: err }) => {
            if (!mounted) return;
            if (err) setError(err);
            else setProducts(items);
            setLoading(false);
        });
        return () => { mounted = false; };
    }, []);

    /* Real-time like counts listener for all loaded products */
    useEffect(() => {
        if (products.length === 0) return;
        const unsubs = products.map((p) => {
            const ref = doc(db, 'likes', p.id);
            return onSnapshot(ref, (snap) => {
                setLikeCounts(prev => ({
                    ...prev,
                    [p.id]: snap.exists() ? (snap.data().count || 0) : 0,
                }));
            });
        });
        return () => unsubs.forEach(u => u());
    }, [products]);

    /* Load per-user liked state from Firestore */
    useEffect(() => {
        if (!currentUser?.uid || products.length === 0) return;

        let cancelled = false;
        const loadUserLikes = async () => {
            try {
                const pairs = await Promise.all(products.map(async (p) => {
                    const userLikeRef = doc(db, 'likes', p.id, 'users', currentUser.uid);
                    const snap = await getDoc(userLikeRef);
                    return [p.id, snap.exists()];
                }));
                if (cancelled) return;
                const likedSet = new Set(
                    pairs.filter(([, liked]) => liked).map(([productId]) => productId)
                );
                setLikedProducts(likedSet);
            } catch (err) {
                console.error('Failed to load user likes:', err);
            }
        };

        loadUserLikes();
        return () => { cancelled = true; };
    }, [currentUser?.uid, products]);

    /* Migrate guest localStorage likes to Firestore on login */
    useEffect(() => {
        if (!currentUser?.uid || products.length === 0) return;

        const localLiked = [...getSavedLikes()];
        if (localLiked.length === 0) return;

        const validProductIds = new Set(products.map((p) => p.id));
        const toMigrate = localLiked.filter((id) => validProductIds.has(id));
        if (toMigrate.length === 0) {
            saveLikes(new Set());
            return;
        }

        let cancelled = false;
        const migrateLikes = async () => {
            try {
                await Promise.all(
                    toMigrate.map((productId) =>
                        toggleLikeForUser(productId, currentUser.uid, true)
                    )
                );
                if (cancelled) return;
                saveLikes(new Set()); // clear only after successful migration
                setLikedProducts((prev) => {
                    const merged = new Set(prev);
                    toMigrate.forEach((id) => merged.add(id));
                    return merged;
                });
            } catch (err) {
                console.error('Failed to migrate local likes:', err);
            }
        };

        migrateLikes();
        return () => { cancelled = true; };
    }, [currentUser?.uid, products]);

    const categories = [
        'All Products',
        'Clothing',
        'Accessories',
        'Printed Materials',
        'Promotional Materials',
        'Packaging & Display',
        'Full Sublimation',
    ];

    const filteredProducts = activeFilter === 'All Products'
        ? products
        : products.filter(p => p.category === activeFilter);

    const visibleProducts = filteredProducts.slice(0, visibleCount);
    const hasMore = visibleCount < filteredProducts.length;

    const handleFilterChange = (category) => {
        setActiveFilter(category);
        setVisibleCount(9);
    };

    const toggleLike = useCallback(async (productId, e) => {
        if (e) e.stopPropagation();
        if (!currentUser?.uid) return; // guests cannot like
        const alreadyLiked = likedProducts.has(productId);
        const newSet = new Set(likedProducts);
        if (alreadyLiked) newSet.delete(productId);
        else newSet.add(productId);
        setLikedProducts(newSet);
        await toggleLikeForUser(productId, currentUser.uid, !alreadyLiked);
    }, [likedProducts, currentUser?.uid]);

    const handleExpandImage = (product, e) => {
        if (e) e.stopPropagation();
        setExpandedImage(product);
    };
    const closeExpandedView = () => setExpandedImage(null);

    if (loading) {
        return (
            <div className="collections-page">
                <div className="collections-header">
                    <h1 className="collections-title">JG Collections</h1>
                    <p className="collections-subtitle">
                        Our Design Gallery — Explore our previous works and get inspired by our past custom prints!
                    </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '80px 0', color: '#888', fontSize: 14 }}>
                    <div style={{ width: 36, height: 36, border: '3px solid #e0e0e0', borderTopColor: '#2c3e50', borderRadius: '50%', animation: 'cp-spin 0.8s linear infinite' }} />
                    <span>Loading collections…</span>
                    <style>{`@keyframes cp-spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            </div>
        );
    }

    return (
        <div className="collections-page">
            {/* Page Header */}
            <div className="collections-header">
                <h1 className="collections-title">JG Collections</h1>
                <p className="collections-subtitle">
                    Our Design Gallery — Explore our previous works and get inspired by our past custom prints!
                </p>
            </div>

            {error && (
                <p style={{ textAlign: 'center', color: '#c62828', fontSize: 14, marginBottom: 24 }}>
                    Could not load collections. Please try again later.
                </p>
            )}

            {/* Category Filters */}
            <div className="category-filters">
                {categories.map((category) => (
                    <button
                        key={category}
                        className={`filter-btn ${activeFilter === category ? 'active' : ''}`}
                        onClick={() => handleFilterChange(category)}
                    >
                        {category}
                    </button>
                ))}
            </div>

            {/* Empty state */}
            {!error && filteredProducts.length === 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '80px 0', color: '#aaa', textAlign: 'center' }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <path d="M21 15l-5-5L5 21"/>
                    </svg>
                    <p style={{ margin: 0, fontSize: 15 }}>
                        {activeFilter === 'All Products'
                            ? 'No collections available yet. Check back soon!'
                            : `No collections in "${activeFilter}" yet.`}
                    </p>
                </div>
            )}

            {/* Product Grid */}
            {visibleProducts.length > 0 && (
                <div className="products-grid">
                    {visibleProducts.map((product) => (
                        <div key={product.id} className="product-card">
                            <div className="product-image-container">
                                <img
                                    src={product.imageUrl}
                                    alt={product.title}
                                    className="product-image"
                                />
                                <div className="product-overlay">
                                    <div className="product-title-container">
                                        <span className="product-title-overlay">{product.title}</span>
                                    </div>
                                    <div className="product-actions">
                                        {/* ── Only show heart if logged in ── */}
                                        {currentUser && (
                                            <button
                                                className={`action-btn ${likedProducts.has(product.id) ? 'liked' : ''}`}
                                                onClick={(e) => toggleLike(product.id, e)}
                                                aria-label="Add to favorites"
                                            >
                                                <HeartIcon filled={likedProducts.has(product.id)} />
                                            </button>
                                        )}
                                        <button
                                            className="action-btn"
                                            onClick={(e) => handleExpandImage(product, e)}
                                            aria-label="Expand view"
                                        >
                                            <SearchIcon />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Load More */}
            {hasMore && (
                <div className="load-more-container">
                    <button
                        className="load-more-btn"
                        onClick={() => setVisibleCount(c => c + 9)}
                    >
                        Load More
                    </button>
                </div>
            )}

            {/* Scroll to Top */}
            <button
                className="scroll-top-btn"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                aria-label="Scroll to top"
            >
                <ArrowUpIcon />
            </button>

            {/* Expanded Image Modal */}
            {expandedImage && (
                <ImageModal
                    product={expandedImage}
                    onClose={closeExpandedView}
                    liked={likedProducts.has(expandedImage.id)}
                    likeCount={likeCounts[expandedImage.id] || 0}
                    onToggleLike={toggleLike}
                    currentUser={currentUser}
                />
            )}
        </div>
    );
}

/* ─── Image Modal ─────────────────────────────────────────────── */
function ImageModal({ product, onClose, liked, likeCount, onToggleLike, currentUser }) {
    const [orientation, setOrientation] = useState('landscape');

    const handleImageLoad = useCallback((e) => {
        const { naturalWidth, naturalHeight } = e.target;
        setOrientation(naturalHeight > naturalWidth ? 'portrait' : 'landscape');
    }, []);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    return (
        <div className="image-modal" onClick={onClose}>
            <div
                className={`modal-content modal-content--${orientation}`}
                onClick={(e) => e.stopPropagation()}
            >
                <button className="modal-close" onClick={onClose} aria-label="Close">
                    <CloseIcon />
                </button>

                <div className="modal-img-wrap">
                    <img
                        src={product.imageUrl}
                        alt={product.title}
                        onLoad={handleImageLoad}
                    />
                </div>

                <div className="modal-info-row">
                    <div className="modal-info-left">
                        <span className="modal-info-title">{product.title}</span>
                        <span className="modal-info-location">
                            <PinIcon />
                            JG Printing Collections
                        </span>
                    </div>
                    <div className="modal-info-right">
                        <span className="modal-info-category">{product.category}</span>
                        {/* ── Like count visible to all, but button only for logged-in users ── */}
                        {currentUser ? (
                            <button
                                className={`modal-like-btn ${liked ? 'liked' : ''}`}
                                onClick={(e) => onToggleLike(product.id, e)}
                                aria-label="Like this"
                            >
                                <HeartIcon filled={liked} />
                                <span>{likeCount} {likeCount === 1 ? 'like' : 'likes'}</span>
                            </button>
                        ) : (
                            <span className="modal-like-count" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <HeartIcon filled={false} />
                                {likeCount} {likeCount === 1 ? 'like' : 'likes'}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ─── Icon Components ─────────────────────────────────────────── */
function HeartIcon({ filled }) {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24"
            fill={filled ? "currentColor" : "none"}
            stroke="currentColor" strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
        </svg>
    );
}

function SearchIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
        </svg>
    );
}

function ArrowUpIcon() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 19V5M5 12l7-7 7 7"/>
        </svg>
    );
}

function CloseIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
    );
}

function PinIcon() {
    return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
        </svg>
    );
}

export default CollectionsPage;
